
import { create } from 'zustand';
import { ToastType } from '../components/Toast';
import { Category, RecommendationStatus } from '../types';

interface HistoryState {
    add_modal?: boolean;
    profile_modal?: boolean;
    support_modal?: boolean;
    admin_modal?: boolean;
    filter_modal?: boolean;
    install_help?: boolean;
    welcome_modal?: boolean;
    notifications_modal?: boolean;
    venue?: string;
    view_mode?: 'map' | 'list';
}

interface UIState {
    // Modals
    isAddModalOpen: boolean;
    isProfileOpen: boolean;
    isSupportModalOpen: boolean;
    isAdminPanelOpen: boolean;
    isFilterOpen: boolean;
    isWelcomeModalOpen: boolean;
    isInstallHelpOpen: boolean;
    isNotificationsOpen: boolean;

    // Notifications
    unreadNotificationCount: number;

    // Selection
    selectedVenueId: string | null;
    listSearchTerm: string;

    // View
    viewMode: 'map' | 'list';

    // Filters
    filters: {
        categories: Category[];
        statuses: RecommendationStatus[];
        userIds: string[];
    };

    // Toast
    toast: {
        message: string;
        type: ToastType;
        isVisible: boolean;
        action?: { label: string; onClick: () => void };
        duration?: number;
    };
    showToast: (message: string, type: ToastType, options?: { action?: { label: string; onClick: () => void }; duration?: number }) => void;
    hideToast: () => void;

    // Actions
    openAddModal: () => void;
    closeAddModal: () => void;
    openProfile: () => void;
    closeProfile: () => void;
    openSupport: () => void;
    closeSupport: () => void;
    openAdmin: () => void;
    closeAdmin: () => void;
    openFilter: () => void;
    closeFilter: () => void;
    openWelcome: () => void;
    closeWelcome: () => void;
    openInstallHelp: () => void;
    closeInstallHelp: () => void;
    openNotifications: () => void;
    closeNotifications: () => void;
    setUnreadNotificationCount: (count: number) => void;

    selectVenue: (id: string | null) => void;
    setListSearchTerm: (term: string) => void;

    transitionAddModalToVenue: (venueId: string) => void;

    setViewMode: (mode: 'map' | 'list') => void;
    setFilters: (filters: UIState['filters']) => void;

    // History Sync
    handlePopState: (state: any) => void;

    reset: () => void;
}

const pushHistory = (key: string, value: boolean | string) => {
    const currentState = window.history.state || {};
    const newState = { ...currentState, [key]: value };
    window.history.pushState(newState, '', '');
};

const replaceHistory = (key: string, value: boolean | string) => {
    const currentState = window.history.state || {};
    const newState = { ...currentState, [key]: value };
    window.history.replaceState(newState, '', '');
};

const removeHistory = (key: string) => {
    const currentState = window.history.state || {};
    if (currentState[key] === undefined) return;
    
    const newState = { ...currentState };
    delete newState[key];
    window.history.replaceState(newState, '', '');
};

export const useUIStore = create<UIState>((set, get) => ({
    isAddModalOpen: false,
    isProfileOpen: false,
    isSupportModalOpen: false,
    isAdminPanelOpen: false,
    isFilterOpen: false,
    isWelcomeModalOpen: false,
    isInstallHelpOpen: false,
    isNotificationsOpen: false,
    unreadNotificationCount: 0,
    selectedVenueId: null,
    listSearchTerm: '',
    viewMode: 'map',
    filters: {
        categories: [],
        statuses: [],
        userIds: []
    },
    toast: {
        message: '',
        type: 'info',
        isVisible: false
    },

    reset: () => {
        set({
            isAddModalOpen: false,
            isProfileOpen: false,
            isSupportModalOpen: false,
            isAdminPanelOpen: false,
            isFilterOpen: false,
            isWelcomeModalOpen: false,
            isInstallHelpOpen: false,
            isNotificationsOpen: false,
            unreadNotificationCount: 0,
            selectedVenueId: null,
            listSearchTerm: '',
            viewMode: 'map',
            filters: {
                categories: [],
                statuses: [],
                userIds: []
            },
            toast: {
                message: '',
                type: 'info',
                isVisible: false,
                action: undefined,
                duration: undefined,
            }
        });
    },

    openAddModal: () => {
        set({ isAddModalOpen: true });
        pushHistory('add_modal', true);
    },
    closeAddModal: () => {
        set({ isAddModalOpen: false });
        removeHistory('add_modal');
    },

    openProfile: () => {
        set({ isProfileOpen: true });
        pushHistory('profile_modal', true);
    },
    closeProfile: () => {
        set({ isProfileOpen: false });
        removeHistory('profile_modal');
    },

    openSupport: () => {
        set({ isSupportModalOpen: true });
        pushHistory('support_modal', true);
    },
    closeSupport: () => {
        set({ isSupportModalOpen: false });
        removeHistory('support_modal');
    },

    openAdmin: () => {
        set({ isAdminPanelOpen: true });
        pushHistory('admin_modal', true);
    },
    closeAdmin: () => {
        set({ isAdminPanelOpen: false });
        removeHistory('admin_modal');
    },

    openFilter: () => {
        set({ isFilterOpen: true });
        pushHistory('filter_modal', true);
    },
    closeFilter: () => {
        set({ isFilterOpen: false });
        removeHistory('filter_modal');
    },

    openWelcome: () => {
        set({ isWelcomeModalOpen: true });
        // No history push - welcome is a one-time overlay, not a navigation target
    },
    closeWelcome: () => {
        set({ isWelcomeModalOpen: false });
    },

    openInstallHelp: () => {
        set({ isInstallHelpOpen: true });
        pushHistory('install_help', true);
    },
    closeInstallHelp: () => {
        set({ isInstallHelpOpen: false });
        removeHistory('install_help');
    },

    openNotifications: () => {
        set({ isNotificationsOpen: true });
        pushHistory('notifications_modal', true);
    },
    closeNotifications: () => {
        set({ isNotificationsOpen: false });
        removeHistory('notifications_modal');
    },
    setUnreadNotificationCount: (count) => {
        set({ unreadNotificationCount: count });
    },

    selectVenue: (id) => {
        set({ selectedVenueId: id });
        if (id) {
            pushHistory('venue', id);
        } else {
             removeHistory('venue');
        }
    },

    setListSearchTerm: (term) => set({ listSearchTerm: term }),

    transitionAddModalToVenue: (venueId) => {
        // Use replaceState intentionally: after submission, "back" should not 
        // reopen the add modal but should close the venue drawer instead.
        set({ isAddModalOpen: false, selectedVenueId: venueId, viewMode: 'map' });
        const currentState = window.history.state || {};
        const newState = { ...currentState };
        delete newState.add_modal;
        newState.venue = venueId;
        newState.view_mode = 'map'; // Ensure map view
        window.history.replaceState(newState, '', '');
    },

    setViewMode: (mode) => {
        if (get().viewMode === mode) return;
        set({ viewMode: mode });
        pushHistory('view_mode', mode);
    },

    setFilters: (filters) => {
        set({ filters });
    },

    handlePopState: (historyState: HistoryState | null) => {
        // Reset all modals to false OR value from history
        // This effectively closes modals when 'back' is pressed
        const state = historyState || {};
        set({
            isAddModalOpen: !!state.add_modal,
            isProfileOpen: !!state.profile_modal,
            isSupportModalOpen: !!state.support_modal,
            isAdminPanelOpen: !!state.admin_modal,
            isFilterOpen: !!state.filter_modal,
            isWelcomeModalOpen: !!state.welcome_modal,
            isInstallHelpOpen: !!state.install_help,
            isNotificationsOpen: !!state.notifications_modal,
            selectedVenueId: state.venue || null,
            viewMode: state.view_mode || 'map', // Default to map if not set
        });
    },

    showToast: (message, type, options) => {
        set({ toast: { message, type, isVisible: true, action: options?.action, duration: options?.duration } });
    },
    hideToast: () => {
        set((state) => ({ toast: { ...state.toast, isVisible: false } }));
    }
}));
